import { ChevronDown } from "lucide-react";
import { useEffect, useMemo, useRef, useState } from "react";
import { Button } from "@/components/ui/button";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { cn } from "@/lib/utils";

export interface CategoryOption {
	value: string;
	label: string;
	level: number;
}

interface CategoryFilterComboboxProps {
	options: CategoryOption[];
	value: string;
	onChange: (value: string) => void;
	className?: string;
	/** Only allow selecting leaf nodes (level=3). Non-leaves shown as headers. */
	leavesOnly?: boolean;
	/** Placeholder when nothing selected. Default: "Wszystkie kategorie" */
	placeholder?: string;
	/** Show "all" reset option. Default: true */
	showAll?: boolean;
	/** Show the monospace code prefix (`value`) before the label. Default: true (categories use numeric codes; roles hide it). */
	showCode?: boolean;
}

/** Builds a tree-ordered flat list from API's flat CategoryTreeNode array. */
export function buildCategoryTree(
	nodes: {
		code: string;
		label: string;
		parentCode: string | null;
		level: number;
		sortOrder: number;
	}[],
): CategoryOption[] {
	const byParent = new Map<string | null, typeof nodes>();
	for (const n of nodes) {
		const key = n.parentCode ?? null;
		const arr = byParent.get(key);
		if (arr) arr.push(n);
		else byParent.set(key, [n]);
	}

	const result: CategoryOption[] = [];
	function walk(parentCode: string | null) {
		const children = byParent.get(parentCode);
		if (!children) return;
		children.sort((a, b) => a.sortOrder - b.sortOrder);
		for (const c of children) {
			result.push({ value: c.code, label: c.label, level: c.level });
			walk(c.code);
		}
	}
	walk(null);
	return result;
}

export function CategoryFilterCombobox({
	options,
	value,
	onChange,
	className,
	leavesOnly = false,
	placeholder = "Wszystkie kategorie",
	showAll = true,
	showCode = true,
}: CategoryFilterComboboxProps) {
	const [open, setOpen] = useState(false);
	const [search, setSearch] = useState("");
	const listRef = useRef<HTMLDivElement>(null);
	const searchInputRef = useRef<HTMLInputElement>(null);
	const triggerRef = useRef<HTMLButtonElement>(null);
	const [portalContainer, setPortalContainer] = useState<HTMLElement | null>(null);

	// Wykryj najblizszy Dialog dla Portal container — zapobiega przejeciu focus
	// przez Dialog FocusScope. Ref: radix-ui/primitives#922
	useEffect(() => {
		if (!open || !triggerRef.current) return;
		const dialog = triggerRef.current.closest<HTMLElement>('[role="dialog"]');
		setPortalContainer(dialog);
	}, [open]);

	const maxLevel = useMemo(() => Math.max(...options.map((o) => o.level), 1), [options]);

	const filtered = useMemo(() => {
		if (!search.trim()) return options;
		const q = search.toLowerCase();
		return options.filter((o) => o.label.toLowerCase().includes(q) || o.value.includes(q));
	}, [options, search]);

	const selectedLabel = useMemo(() => {
		if (!value) return undefined;
		const opt = options.find((o) => o.value === value);
		if (!opt) return undefined;
		return showCode ? `${opt.value} ${opt.label}` : opt.label;
	}, [options, value, showCode]);

	const isSelectable = (o: CategoryOption) => !leavesOnly || o.level === maxLevel;

	// Scroll to selected item when popover opens
	useEffect(() => {
		if (!open || !value) return;
		const scrollToSelected = () => {
			const el = listRef.current?.querySelector(`[data-value="${value}"]`);
			if (el) {
				el.scrollIntoView({ block: "center" });
			}
		};
		// Double rAF to ensure Popover portal is fully rendered
		requestAnimationFrame(() => requestAnimationFrame(scrollToSelected));
	}, [open, value]);

	// Block scroll on Sheet's inner scrollable div while Popover is open
	useEffect(() => {
		if (!open) return;
		const sheetContent = document.querySelector("[data-slot='sheet-content']");
		if (!sheetContent) return;
		const scrollable = sheetContent.querySelector(".overflow-y-auto") as HTMLElement | null;
		if (!scrollable) return;
		const prev = scrollable.style.overflow;
		scrollable.style.overflow = "hidden";
		return () => {
			scrollable.style.overflow = prev;
		};
	}, [open]);

	return (
		// WYMAGANE: `modal` prop dla Popover wewnatrz Dialog — bez niego Dialog's
		// DismissableLayer traktuje klik w Popover jako outside i zamyka popover.
		// Kontrakt: wszystkie @radix-ui/react-dismissable-layer w tej samej wersji
		// (package.json overrides). Ref: radix-ui/primitives#2348
		<Popover open={open} onOpenChange={setOpen} modal>
			<PopoverTrigger asChild>
				<Button
					ref={triggerRef}
					variant="outline"
					className={cn("justify-between gap-2 font-normal", className)}
				>
					<span className="truncate">{selectedLabel ?? placeholder}</span>
					<ChevronDown className="size-3.5 shrink-0 opacity-50" />
				</Button>
			</PopoverTrigger>
			<PopoverContent
				portalContainer={portalContainer}
				className="w-[min(360px,calc(100vw-16px))] p-0 pointer-events-auto"
				align="start"
				onOpenAutoFocus={(e) => {
					// Synchronous focus — bez setTimeout zeby Dialog FocusScope nie nadpisal
					e.preventDefault();
					searchInputRef.current?.focus();
				}}
				onCloseAutoFocus={(e) => e.preventDefault()}
			>
				<div className="p-2 border-b">
					<input
						ref={searchInputRef}
						placeholder="Szukaj po numerze lub nazwie..."
						value={search}
						onChange={(e) => setSearch(e.target.value)}
						className="w-full h-8 px-2 text-sm bg-transparent border rounded-md border-input focus:outline-none focus:ring-1 focus:ring-ring"
					/>
				</div>
				<div
					ref={listRef}
					className="max-h-[300px] overflow-y-auto py-1 overscroll-contain"
					style={{ WebkitOverflowScrolling: "touch" }}
					onTouchMove={(e) => e.stopPropagation()}
					onWheel={(e) => e.stopPropagation()}
				>
					{showAll && (
						<button
							type="button"
							className={cn(
								"w-full text-left px-3 py-1.5 text-sm hover:bg-muted/50 transition-colors",
								!value && "bg-primary/5 font-medium",
							)}
							onClick={() => {
								onChange("");
								setOpen(false);
								setSearch("");
							}}
						>
							{placeholder}
						</button>
					)}
					{filtered.map((o) => {
						const selectable = isSelectable(o);
						return (
							<button
								key={o.value}
								type="button"
								data-value={o.value}
								disabled={!selectable}
								className={cn(
									"w-full text-left py-1 text-sm transition-colors",
									selectable && "hover:bg-muted/50 cursor-pointer",
									!selectable && "cursor-default",
									o.level === 1 && "pl-3 font-semibold pt-1.5",
									o.level === 2 && "pl-7 text-muted-foreground",
									o.level === 3 && "pl-11 text-muted-foreground text-xs",
									o.value === value && "bg-primary/5 !text-primary !font-medium",
								)}
								onClick={() => {
									if (!selectable) return;
									onChange(o.value);
									setOpen(false);
									setSearch("");
								}}
							>
								{showCode && (
									<span className="text-muted-foreground/50 font-mono text-[10px] mr-1">
										{o.value}
									</span>
								)}
								{o.label}
							</button>
						);
					})}
					{filtered.length === 0 && (
						<div className="px-3 py-4 text-sm text-muted-foreground text-center">Brak wynikow</div>
					)}
				</div>
			</PopoverContent>
		</Popover>
	);
}
